import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const cardVariants = cva(
  [
    "rounded-xl border",
    "transition-all duration-[200ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
  ].join(" "),
  {
    variants: {
      variant: {
        /** Card base: Dark Tech linear com micro-iluminação superior */
        default: [
          "bg-[hsl(var(--surface))]",
          "border-[hsl(var(--border))]",
          "shadow-[0_1px_2px_0_rgb(0_0_0_/_0.5),_inset_0_1px_0_0_rgba(255,255,255,0.06)]",
        ].join(" "),
        /** Card elevado: mais profundidade e iluminação interna */
        raised: [
          "bg-[hsl(var(--surface-raised))]",
          "border-[hsl(var(--border-strong))]",
          "shadow-[0_4px_16px_0_rgb(0_0_0_/_0.6),_inset_0_1px_0_0_rgba(255,255,255,0.08)]",
        ].join(" "),
        /** Bloco de receita: borda ouro radiante + iluminação sutil */
        revenue: [
          "bg-[hsl(var(--surface))]",
          "border-[hsl(var(--gold)/0.35)]",
          "shadow-[0_0_32px_rgba(251,191,36,0.12),_inset_0_1px_0_0_rgba(251,191,36,0.2)]",
          "hover:border-[hsl(var(--gold)/0.55)]",
          "hover:shadow-[0_0_40px_rgba(251,191,36,0.22)]",
        ].join(" "),
        /** Bloco de lucro/conversão: borda esmeralda elétrica */
        profit: [
          "bg-[hsl(var(--surface))]",
          "border-[hsl(var(--primary)/0.35)]",
          "shadow-[0_0_32px_rgba(16,185,129,0.12),_inset_0_1px_0_0_rgba(16,185,129,0.2)]",
          "hover:border-[hsl(var(--primary)/0.55)]",
          "hover:shadow-[0_0_40px_rgba(16,185,129,0.22)]",
        ].join(" "),
        /** Overlay/modal: superfície mais elevada */
        overlay: [
          "bg-[hsl(var(--surface-overlay))]",
          "border-[hsl(var(--border-strong))]",
          "shadow-[0_12px_36px_0_rgb(0_0_0_/_0.75),_inset_0_1px_0_0_rgba(255,255,255,0.1)]",
        ].join(" "),
        /** Ghost: sem fundo, apenas contorno hairline */
        ghost: [
          "bg-transparent",
          "border-[hsl(var(--border-strong))]",
        ].join(" "),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  /**
   * @deprecated Use variant="raised" em vez de raised={true}.
   * Mantido para compatibilidade com código existente.
   */
  raised?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, raised, ...props }, ref) => {
    const resolvedVariant = raised && !variant ? "raised" : variant;
    return (
      <div
        ref={ref}
        className={cn(cardVariants({ variant: resolvedVariant }), className)}
        {...props}
      />
    );
  },
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1 p-5 pb-3", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-base font-semibold leading-tight tracking-tight text-foreground",
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground leading-relaxed", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-5 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center gap-2 px-5 py-4 border-t border-[hsl(var(--border))]",
      className,
    )}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

/** Label editorial: 11px uppercase tracking-wide, para cabeçalhos de bloco */
const CardLabel = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("label-editorial", className)}
    {...props}
  />
));
CardLabel.displayName = "CardLabel";

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardLabel,
  cardVariants,
};
